import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "./styles/tailwind.css";

import { App } from "./app";
import { t } from "./contract/strings";
import { initializeTheme } from "./state/theme";
import { boot, watchForSignInLinks } from "./boot";
import { showBootFault } from "./boot-fault";
import { bootOverlayUp } from "./boot-overlay";
import { useApp } from "./state/store";
import * as telemetry from "../lib/telemetry.js";

/**
 * Entry point: fault reporting first, then the app, then the boot sequence.
 *
 * The handlers go on before anything else can throw. There is no console on a phone, so
 * an uncaught error that is not put on screen is an app that stopped working and said
 * nothing -- `app.js` learned this and answered it with `reportUncaught`; this is the same
 * answer with somewhere better to land. Two surfaces, because the failure can arrive on
 * either side of the boot overlay: while it is up, its own line is the only thing visible;
 * after it comes down, the banner the router renders.
 *
 * `app.css` is not imported here. It is linked from `index.html` out of `public/`, because
 * the three static pages under `/privacy/`, `/terms/` and `/home/` link to the same file
 * and the bundler never sees them -- and because a stylesheet that arrives with the bundle
 * arrives after the first paint, which is exactly what the boot overlay must not wait for.
 *
 * **The boot-fault panel is not a shell id.** `#boot` and `#boot-text` are declared in
 * `index.html` and the DOM probe holds that contract. The detail paragraph and its
 * disclosure are created here, so `boot-fault.ts` keeps the reference and this file
 * never looks up an id the shell does not have.
 */

function reportUncaught(what: string, message: string) {
  // Capped: the server rejects detail strings over 64 chars, and an error near the
  // result path can carry transcript text in its message -- the one thing telemetry
  // must never hold. The full message still lands on screen via `showBootFault`.
  telemetry.log("error", { what, message: message.slice(0, 64) });
  useApp.getState().reportFault(what, message);
  const overlay = document.getElementById("boot");
  const text = document.getElementById("boot-text");
  // Still booting: the banner is behind a full-screen overlay, so say it here instead.
  if (bootOverlayUp(overlay) && text) {
    text.textContent = t("app.screen_broke");
    showBootFault(text, t("state.fault", { what, message }));
  }
}

window.addEventListener("error", (e) => {
  reportUncaught(t("app.something_broke"), e.message || String(e.error));
});
window.addEventListener("unhandledrejection", (e) => {
  reportUncaught(t("app.something_broke"), e.reason?.message || String(e.reason));
});

/**
 * The build id, where `lib/claims.js` can find it.
 *
 * Assigned rather than substituted into that file: it reads `globalThis.__MEETFOLKS_BUILD__`
 * at call time so it stays runnable outside the bundle, which the pytest interop probes
 * depend on. Set here, at startup, long before any claim is built.
 */
declare const __MEETFOLKS_BUILD__: string;
(globalThis as unknown as { __MEETFOLKS_BUILD__?: string }).__MEETFOLKS_BUILD__ = __MEETFOLKS_BUILD__;

/**
 * The commit this build came from, where `lib/api.js` can find it for the
 * `X-MeetFolks-Build` header (#980). Assigned, not imported, for the same reason as the
 * build id: the lib reads it at call time and stays runnable outside the bundle.
 */
declare const __MEETFOLKS_COMMIT__: string;
(globalThis as unknown as { __MEETFOLKS_COMMIT__?: string }).__MEETFOLKS_COMMIT__ =
  __MEETFOLKS_COMMIT__;

const root = document.getElementById("root");
if (!root) throw new Error(t("boot.mount_missing"));

initializeTheme();

/**
 * The parity harness owns its own router (#575), so it is mounted *instead of* the app
 * rather than as a route inside the app's router: two routers cannot nest. It is a
 * development-only page and is absent from a release build. Keeping the render behind
 * the `DEV` branch keeps the harness and its fixture globs out of the production bundle.
 */
if (import.meta.env.DEV && location.pathname === "/parity") {
  import("./routes/parity")
    .then(({ Parity }) => {
      createRoot(root).render(
        <StrictMode>
          <Parity />
        </StrictMode>
      );
    })
    .catch((error: unknown) => {
      // `boot=skip` has already hidden the overlay `reportUncaught` writes into, so a
      // harness that failed to load was a blank page. Say so where the gallery waits (#615).
      document.body.dataset.parityError = error instanceof Error ? error.message : String(error);
      console.error(error);
    });
} else {
  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}

/**
 * The kitchen sink's side-by-side view mounts two same-origin iframes of this same document,
 * because the palette lives on `:root` and two themes need two documents. Each one is a full
 * app, and [V] each one booted: three ORT inference sessions and three 84 MB model loads in
 * one tab. The frames only need the components rendered, so they say so and this skips the
 * whole sequence -- and takes the overlay down itself, since nothing else will.
 */
const galleryFrame =
  import.meta.env.DEV && new URLSearchParams(location.search).get("boot") === "skip";

watchForSignInLinks();
if (galleryFrame) {
  const overlay = document.getElementById("boot");
  if (overlay) overlay.hidden = true;
} else {
  void boot();
}
